// /m/:code — the court's public summary (K6). No account: the court's staff
// unlock it with the SAME staff PIN they use on the kiosk phone, then see the
// day's sessions, what was charged, the court's side (derived by the server
// and labelled so), their own comps, and the last settlement. Read-only,
// plain, mobile-first. The session lasts 18 hours in this browser.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { courtInfo, courtUnlock, courtSummary, courtStatements, courtStatement, errDetail, errMsg } from '../kiosk/kioskApi';
import { LiveCard } from '../kiosk/KioskLive';
import { StatementModal } from '../kiosk/KioskStatement';
import { useAutoRefresh } from '../kiosk/useAutoRefresh';
import { fmtHrMin, fmtBookedPlayed } from '../liveUi';

const BG = '#0A0A0A';
const CARD = '#141414';
const LINE = '#222';
const TEXT = '#F5F5F5';
const MUTED = '#9A9A9A';
const GREEN = '#00E676';
const AMBER = '#FFB300';
const RED = '#FF2D55';

const peso = (c) => {
  if (c == null || Number.isNaN(Number(c))) return '—';
  const n = Number(c) / 100;
  const s = Math.abs(n).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return n < 0 ? `−₱${s}` : `₱${s}`;
};
const hhmm = (ms) => (ms ? new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—');
const dmy = (ms) => (ms ? new Date(ms).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const shiftYmd = (ymd, days) => {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(y, m - 1, d + days);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};
const epochDayToDate = (day) => (day == null ? '—' : dmy(Number(day) * 86400000));

const STATUS_LABEL = { CHARGED: 'Charged', VOID: 'Not charged', RUNNING: 'Running' };
const STOP_LABEL = {
  WEATHER: 'Weather', MACHINE_FAULT: 'Machine fault', KIOSK_FAULT: 'Kiosk fault', CUSTOMER_LEFT: 'Customer left',
  COURT_COMP: 'Court comp', OWNER_COMP: 'Owner comp', TEST: 'Test', OTHER: 'Other',
};

const btn = (primary = false) => ({
  width: '100%', padding: '14px 16px', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer',
  background: primary ? '#E63946' : 'transparent', color: primary ? '#fff' : MUTED,
  border: primary ? 'none' : `1px solid ${LINE}`,
});
const cardStyle = { background: CARD, border: `1px solid ${LINE}`, borderRadius: 14, padding: 16 };

function storageKey(code) { return `kiosk_court_${code}`; }
function loadSession(code) {
  try {
    const s = JSON.parse(sessionStorage.getItem(storageKey(code)) || 'null');
    if (s?.session_token && (!s.expires_at || s.expires_at > Date.now())) return s;
  } catch { /* ignore */ }
  return null;
}

export default function SportsCourtSummaryPage() {
  const { code: rawCode } = useParams();
  const code = (rawCode || '').toUpperCase();
  const [info, setInfo] = useState(null);
  const [infoErr, setInfoErr] = useState('');
  const [session, setSession] = useState(() => loadSession(code));
  const [pin, setPin] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockErr, setUnlockErr] = useState(null);     // { message, lockedUntil? }
  const [date, setDate] = useState(todayYmd());
  const [summary, setSummary] = useState(null);
  const [summaryErr, setSummaryErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const [statements, setStatements] = useState(null);        // 7.0f: every settlement of this phone, court audience
  const [statementsErr, setStatementsErr] = useState('');
  const [statement, setStatement] = useState(null);          // { title, load } for the open statement

  useEffect(() => { document.title = 'Court summary'; }, []);
  useEffect(() => {
    courtInfo(code).then(setInfo).catch(e => setInfoErr(errMsg(e, 'This link is not valid.')));
  }, [code]);
  useEffect(() => {
    const t = setInterval(() => setTick(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const lock = useCallback(() => {
    sessionStorage.removeItem(storageKey(code));
    setSession(null);
    setSummary(null);
    setStatements(null);
    setStatement(null);
    setPin('');
  }, [code]);

  // 7.0f: the settlements list rides the same session; it does not need the day.
  const loadStatements = useCallback(async (tok) => {
    if (!tok) return;
    setStatementsErr('');
    try { setStatements(await courtStatements(code, tok)); }
    catch (e) { if (e?.response?.status !== 401) setStatementsErr(errMsg(e, 'Could not load the statements.')); }
  }, [code]);

  const loadSummary = useCallback(async (tok, ymd, silent = false) => {
    if (!tok) return;
    if (!silent) { setLoading(true); setSummaryErr(''); }
    try {
      setSummary(await courtSummary(code, tok, ymd));
    } catch (e) {
      const d = errDetail(e);
      if (e?.response?.status === 401) {
        lock();
        setUnlockErr({ message: (d && d.message) || 'Unlock again with the staff PIN.' });
      } else {
        setSummaryErr(errMsg(e, 'Could not load the summary.'));
      }
    } finally { setLoading(false); }
  }, [code, lock]);

  const sessionToken = session?.session_token;
  useEffect(() => { if (sessionToken) loadSummary(sessionToken, date); }, [sessionToken, date, loadSummary]);
  useEffect(() => { if (sessionToken) loadStatements(sessionToken); }, [sessionToken, loadStatements]);
  const openStatement = (x) => setStatement({
    title: `Statement · ${x.period_from} – ${x.period_to}`,
    load: () => courtStatement(code, sessionToken, x.id),
  });
  // While unlocked: follow the phone without a reload (a 401 re-locks via loadSummary).
  useAutoRefresh(() => loadSummary(sessionToken, date, true), 30000, !!sessionToken);

  const unlock = async () => {
    const p = pin.trim();
    if (!p) return;
    setUnlocking(true);
    setUnlockErr(null);
    try {
      const r = await courtUnlock(code, p);
      const s = { session_token: r.session_token, expires_at: Date.now() + (r.expires_in_s || 0) * 1000 };
      sessionStorage.setItem(storageKey(code), JSON.stringify(s));
      setSession(s);
      setPin('');
    } catch (e) {
      const d = errDetail(e) || {};
      setUnlockErr({
        code: d.code,
        message: d.message || errMsg(e, 'Could not unlock.'),
        lockedUntil: d.locked_until_ms || null,
        triesLeft: d.tries_left,
      });
    } finally { setUnlocking(false); }
  };

  const lockedMinutes = useMemo(() => {
    if (!unlockErr?.lockedUntil) return 0;
    return Math.max(0, Math.ceil((unlockErr.lockedUntil - tick) / 60000));
  }, [unlockErr, tick]);

  const title = info ? (info.court_name || info.site_name || info.name) : 'Court';

  // ── Link not valid ──────────────────────────────────────────────────────────
  if (infoErr) {
    return (
      <div style={{ minHeight: '100vh', background: BG, color: TEXT, padding: 24, fontFamily: 'system-ui, sans-serif' }} data-testid="court-page-invalid">
        <div style={{ ...cardStyle, maxWidth: 480, margin: '40px auto', textAlign: 'center' }}>
          <p style={{ fontSize: 18, fontWeight: 700 }}>This link does not work</p>
          <p style={{ color: MUTED, marginTop: 8, lineHeight: 1.5 }}>{infoErr}</p>
          <p style={{ color: MUTED, marginTop: 8, fontSize: 13 }}>Ask the machine owner for the current link.</p>
        </div>
      </div>
    );
  }

  // ── PIN screen ──────────────────────────────────────────────────────────────
  if (!session) {
    const locked = lockedMinutes > 0;
    return (
      <div style={{ minHeight: '100vh', background: BG, color: TEXT, padding: 20, fontFamily: 'system-ui, sans-serif' }} data-testid="court-page-pin">
        <div style={{ maxWidth: 420, margin: '24px auto' }}>
          <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946', fontWeight: 700 }}>Ball machine</p>
          <h1 style={{ fontSize: 26, fontWeight: 800, marginTop: 4 }}>{title}</h1>
          {info && (info.site_name || info.name) && (
            <p style={{ color: MUTED, marginTop: 2 }}>{[info.site_name, info.name].filter(Boolean).join(' · ')}</p>
          )}

          <div style={{ ...cardStyle, marginTop: 20 }}>
            {info && !info.pin_ready ? (
              <p style={{ color: AMBER, lineHeight: 1.5 }} data-testid="court-pin-not-ready">
                The machine has not shared its staff PIN with the server yet. Set the staff PIN on the machine and let it come online once, then try again.
              </p>
            ) : (
              <>
                <label htmlFor="court-pin" style={{ display: 'block', fontSize: 13, color: MUTED, marginBottom: 8 }}>
                  Enter the staff PIN — the same one you use on the machine.
                </label>
                <input id="court-pin" data-testid="court-pin-input" type="password" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                  value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))} disabled={locked || unlocking}
                  onKeyDown={e => { if (e.key === 'Enter') unlock(); }} placeholder="••••"
                  style={{ width: '100%', boxSizing: 'border-box', background: BG, color: TEXT, border: `1px solid ${LINE}`, borderRadius: 12,
                    padding: '14px 16px', fontSize: 28, letterSpacing: 10, textAlign: 'center', outline: 'none' }} />
                {unlockErr && (
                  <p data-testid="court-pin-error" style={{ color: locked ? AMBER : RED, marginTop: 10, lineHeight: 1.5, fontSize: 14 }}>
                    {locked ? `Too many wrong PINs. Try again in ${lockedMinutes} minute${lockedMinutes === 1 ? '' : 's'}.` : unlockErr.message}
                    {!locked && unlockErr.triesLeft != null && unlockErr.code === 'pin_wrong' && (
                      <span style={{ color: MUTED }}> {unlockErr.triesLeft} {unlockErr.triesLeft === 1 ? 'try' : 'tries'} left before a 15-minute lock.</span>
                    )}
                  </p>
                )}
                <div style={{ marginTop: 14 }}>
                  <button onClick={unlock} disabled={locked || unlocking || !pin} data-testid="court-pin-submit"
                    style={{ ...btn(true), opacity: locked || unlocking || !pin ? 0.5 : 1 }}>
                    {unlocking ? 'Checking…' : 'Open'}
                  </button>
                </div>
              </>
            )}
          </div>
          <p style={{ color: '#555', fontSize: 12, marginTop: 14, lineHeight: 1.5 }}>
            Five wrong PINs lock this link for 15 minutes, then an hour. Only the court&apos;s own figures are shown here.
          </p>
        </div>
      </div>
    );
  }

  // ── Summary ─────────────────────────────────────────────────────────────────
  const s = summary;
  const totals = s?.totals;
  return (
    <div style={{ minHeight: '100vh', background: BG, color: TEXT, padding: 16, fontFamily: 'system-ui, sans-serif' }} data-testid="court-page-summary">
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: '#E63946', fontWeight: 700 }}>Ball machine</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{s?.device?.court_name || s?.device?.site_name || title}</h1>
          </div>
          <button onClick={lock} data-testid="court-lock" style={{ ...btn(false), width: 'auto', padding: '8px 12px', fontSize: 13 }}>Lock</button>
        </div>

        {/* K8 P2.5 P5: the machine in use RIGHT NOW, whatever day is being viewed
            below. The same card the court sees when signed in; the server sends
            the court's figures only. Refreshes with the summary every 30 s and
            ticks on its own in between. */}
        {s?.live && (
          <div style={{ marginTop: 14 }} data-testid="court-live-wrap">
            <LiveCard live={s.live} audience="court" deviceName={s.device?.name} siteName={s.device?.site_name} testId="court-live" />
          </div>
        )}

        {/* Day picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14 }}>
          <button onClick={() => setDate(d => shiftYmd(d, -1))} data-testid="court-day-prev" style={{ ...btn(false), width: 48, padding: '10px 0', fontSize: 18 }} aria-label="Previous day">‹</button>
          <input type="date" value={date} max={todayYmd()} onChange={e => e.target.value && setDate(e.target.value)} data-testid="court-day-input"
            style={{ flex: 1, background: CARD, color: TEXT, border: `1px solid ${LINE}`, borderRadius: 12, padding: '10px 12px', fontSize: 16, textAlign: 'center' }} />
          <button onClick={() => setDate(d => shiftYmd(d, 1))} disabled={date >= todayYmd()} data-testid="court-day-next"
            style={{ ...btn(false), width: 48, padding: '10px 0', fontSize: 18, opacity: date >= todayYmd() ? 0.4 : 1 }} aria-label="Next day">›</button>
        </div>

        {summaryErr && <p style={{ color: RED, marginTop: 12 }}>{summaryErr}</p>}
        {loading && !s && <p style={{ color: MUTED, marginTop: 20, textAlign: 'center' }}>Loading…</p>}

        {s && (
          <>
            {/* Totals */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 14 }} data-testid="court-totals">
              <div style={cardStyle}>
                <p style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>Sessions</p>
                <p style={{ fontSize: 22, fontWeight: 800 }}>{totals.sessions}</p>
              </div>
              <div style={cardStyle}>
                <p style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>Charged</p>
                <p style={{ fontSize: 18, fontWeight: 800 }}>{peso(totals.charged_centavos)}</p>
              </div>
              <div style={cardStyle}>
                <p style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>Your share</p>
                <p style={{ fontSize: 18, fontWeight: 800, color: totals.court_side_centavos < 0 ? AMBER : GREEN }}>{peso(totals.court_side_centavos)}</p>
                <p style={{ fontSize: 10, color: MUTED }}>{totals.court_side_complete ? 'derived' : 'partial — see settlement'}</p>
              </div>
            </div>

            {/* Sessions */}
            <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: MUTED, marginTop: 20, marginBottom: 8 }}>Sessions</h2>
            {s.sessions.length === 0 ? (
              <div style={{ ...cardStyle, textAlign: 'center', color: MUTED }} data-testid="court-no-sessions">No sessions on this day.</div>
            ) : s.sessions.map(x => {
              const running = x.status === 'RUNNING';
              // T1: booked and played; the old billed duration until the server sends both. The server now
              // sends the contract's booked_minutes beside played_minutes on this item (it used to send the
              // raw booking), and requiring both fields is kept as the guard for an older server.
              const legacyMin = x.billed_minutes ?? x.minutes;
              const time = fmtBookedPlayed(x.booked_minutes, x.played_minutes, x.mode) ?? (legacyMin != null ? fmtHrMin(legacyMin) : null);
              return (
                <div key={x.row_id} style={{ ...cardStyle, marginBottom: 8, borderColor: x.court_comp ? AMBER + '60' : LINE }} data-testid={`court-session-${x.row_id}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <p style={{ fontSize: 17, fontWeight: 700 }}>
                        {hhmm(x.started_at_ms)} – {running ? 'now' : hhmm(x.ended_at_ms)}
                        {time != null && <span style={{ color: MUTED, fontWeight: 400, fontSize: 14 }} data-testid={`court-session-time-${x.row_id}`}> · {time}</span>}
                      </p>
                      {(x.customer_name || x.trial) && (
                        <p style={{ fontSize: 14, fontWeight: 700, marginTop: 1 }} data-testid={`court-session-name-${x.row_id}`}>
                          {x.customer_name || ''}{x.customer_name && x.trial ? ' · ' : ''}{x.trial ? <span style={{ color: '#34C759' }}>Free trial</span> : null}
                        </p>
                      )}
                      <p style={{ fontSize: 13, color: MUTED, marginTop: 2 }}>
                        {STATUS_LABEL[x.status] || x.status || '—'}
                        {x.mode ? ` · ${x.mode === 'OPEN' ? 'open time' : 'fixed time'}` : ''}
                        {x.stop_reason ? ` · ${STOP_LABEL[x.stop_reason] || x.stop_reason}` : ''}
                      </p>
                      {x.court_comp && <p style={{ fontSize: 12, color: AMBER, fontWeight: 700, marginTop: 4 }} data-testid="court-comp-flag">Court comp — you granted this session</p>}
                      {x.note && <p style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>{x.note}</p>}
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 96 }}>
                      <p style={{ fontSize: 12, color: MUTED }}>charged</p>
                      <p style={{ fontSize: 16, fontWeight: 700 }}>{running ? '—' : peso(x.charged_centavos)}</p>
                      {x.voucher && (
                        <p style={{ fontSize: 11, color: x.voucher.consumed ? '#4FC3F7' : MUTED }} data-testid={`court-voucher-${x.row_id}`}>
                          voucher {x.voucher.code}{x.voucher.consumed
                            ? ` −${peso(x.voucher.discount_centavos)} · your part ${peso(x.voucher.court_funded_centavos)}`
                            : x.voucher.released ? ' returned' : x.voucher.state === 'CLAIMED' ? ' claimed' : ''}
                        </p>
                      )}
                      <p style={{ fontSize: 12, color: MUTED, marginTop: 4 }}>your share</p>
                      {x.court_side_derived ? (
                        <p style={{ fontSize: 16, fontWeight: 700, color: x.court_side_centavos < 0 ? AMBER : GREEN }}>{peso(x.court_side_centavos)}</p>
                      ) : (
                        <p style={{ fontSize: 12, color: MUTED }}>{x.court_side_note || 'see settlement'}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {((totals.voucher_discount_count ?? totals.vouchers) > 0 || totals.voucher_discount_centavos > 0) && (
              <div style={{ ...cardStyle, marginTop: 12 }} data-testid="court-vouchers">
                <p style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>Vouchers today</p>
                {/* T1: "4 vouchers · ₱400 off" counts only the vouchers that took money off (voucher_discount_count). */}
                <p style={{ fontSize: 14, marginTop: 4 }}>
                  {totals.voucher_discount_count != null
                    ? `${totals.voucher_discount_count} voucher${totals.voucher_discount_count === 1 ? '' : 's'}`
                    : `${totals.vouchers} used`} · {peso(totals.voucher_discount_centavos)} off
                  <span style={{ color: MUTED }}> · your part {peso(totals.voucher_court_funded_centavos)} · HighMark&apos;s part {peso(totals.voucher_owner_funded_centavos)}</span>
                </p>
                <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>Already inside &quot;Your share&quot;.</p>
              </div>
            )}

            {/* T1: bonus-time vouchers take nothing off — they give machine time — so they read on
                their own, and a day with only free time never shows a block of ₱0.00 figures. */}
            {totals.voucher_bonus_count > 0 && (
              <div style={{ ...cardStyle, marginTop: 12 }} data-testid="court-vouchers-free">
                <p style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase' }}>Free time today</p>
                <p style={{ fontSize: 14, marginTop: 4 }} data-testid="court-vouchers-bonus">
                  {totals.voucher_bonus_count} free-time voucher{totals.voucher_bonus_count === 1 ? '' : 's'} · {fmtHrMin(totals.voucher_bonus_minutes || 0)} free
                </p>
              </div>
            )}

            {/* Flags */}
            {s.flags.length > 0 && (
              <>
                <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: MUTED, marginTop: 20, marginBottom: 8 }}>Your comps today</h2>
                <div style={cardStyle} data-testid="court-flags">
                  {s.flags.map(f => (
                    <p key={f.row_id} style={{ fontSize: 14, padding: '6px 0', borderBottom: `1px solid ${LINE}` }}>
                      {hhmm(f.started_at_ms)} · {STOP_LABEL[f.stop_reason] || f.stop_reason || 'comp'}
                      {f.notional_centavos != null && <span style={{ color: MUTED }}> · would have been {peso(f.notional_centavos)}</span>}
                      {f.court_side_derived && <span style={{ color: AMBER }}> · owner&apos;s share owed {peso(-f.court_side_centavos)}</span>}
                    </p>
                  ))}
                </div>
              </>
            )}

            {/* Last settlement */}
            <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: MUTED, marginTop: 20, marginBottom: 8 }}>Last settlement</h2>
            {s.last_settlement ? (
              <div style={cardStyle} data-testid="court-settlement">
                <p style={{ fontSize: 14 }}>
                  {epochDayToDate(s.last_settlement.period_from_epoch_day)} – {epochDayToDate(s.last_settlement.period_to_epoch_day)}
                  <span style={{ color: MUTED }}> · collected {dmy(s.last_settlement.collected_at_ms)}</span>
                </p>
                <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                  <div><p style={{ fontSize: 11, color: MUTED }}>Sessions</p><p style={{ fontSize: 16, fontWeight: 700 }}>{s.last_settlement.session_count ?? '—'}</p></div>
                  <div><p style={{ fontSize: 11, color: MUTED }}>Gross</p><p style={{ fontSize: 16, fontWeight: 700 }}>{peso(s.last_settlement.gross_centavos)}</p></div>
                  <div><p style={{ fontSize: 11, color: MUTED }}>Court</p><p style={{ fontSize: 16, fontWeight: 700, color: GREEN }}>{peso(s.last_settlement.court_centavos)}</p></div>
                </div>
                {s.last_settlement.note && <p style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>{s.last_settlement.note}</p>}
              </div>
            ) : (
              <div style={{ ...cardStyle, color: MUTED }} data-testid="court-no-settlement">No settlement recorded on the machine yet.</div>
            )}

            {/* 7.0f: every settlement, each with its statement — the same paper HighMark reads, without their figure. */}
            <h2 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, color: MUTED, marginTop: 20, marginBottom: 8 }}>Statements</h2>
            <div style={cardStyle} data-testid="court-statements">
              {statementsErr ? <p style={{ color: RED, fontSize: 14 }}>{statementsErr}</p>
                : !statements ? <p style={{ color: MUTED, fontSize: 14 }}>Loading…</p>
                : statements.statements.length === 0 ? <p style={{ color: MUTED, fontSize: 14 }} data-testid="court-no-statements">No settlement yet.</p>
                : statements.statements.map((x, i) => (
                  <div key={x.id} data-testid={`court-statement-${x.id}`}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: i ? `1px solid ${LINE}` : 'none' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 700 }}>{x.period_from} – {x.period_to}</p>
                      <p style={{ fontSize: 12, color: MUTED }}>
                        {x.session_count ?? '—'} sessions · gross {peso(x.gross_centavos)} · yours <span style={{ color: x.court_centavos < 0 ? AMBER : GREEN }}>{peso(x.court_centavos)}</span>
                        {' · '}{x.source === 'web' ? 'on the web' : 'on the machine'} · {dmy(x.collected_at_ms)}
                        {x.conflict && <span style={{ color: RED, fontWeight: 700 }}> · under review</span>}
                      </p>
                    </div>
                    <button onClick={() => openStatement(x)} data-testid={`court-statement-open-${x.id}`} style={{ ...btn(false), width: 'auto', padding: '8px 12px', fontSize: 13, flexShrink: 0 }}>Statement</button>
                  </div>
                ))}
            </div>

            <p style={{ fontSize: 11, color: '#555', marginTop: 16, lineHeight: 1.5 }}>{s.derived_note}</p>
            <p style={{ fontSize: 11, color: '#444', marginTop: 4 }}>Read-only. Figures are what the machine reported; the machine itself is the record.</p>
          </>
        )}
      </div>
      {statement && <StatementModal title={statement.title} load={statement.load} audience="court" onClose={() => setStatement(null)} />}
    </div>
  );
}
